import React from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, } from "chart.js";
import { FCard } from "../../../components/card";
import { SIconButton } from "../../../components/buttons";
import { InfoCircle } from "iconsax-react";
import TemperatureIcon from "../../../components/icons/TemperatureIcon";
import { centered } from "@ui/components/style/style";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
export const TemperatureChart = ({ data, title = "Temperature", }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // Custom plugin to draw dotted grid lines and colored labels
    const dottedGridPlugin = {
        id: "dottedGrid",
        beforeDraw: (chart) => {
            const ctx = chart.ctx;
            const yScale = chart.scales.y;
            const xScale = chart.scales.x;
            if (!yScale || !xScale)
                return;
            const colors = ["#065E49", "#7A4100", "#7A4100", "#B71D18", "#B71D18"];
            const borderColors = [
                "#22C55E29",
                "#FFAB0029",
                "#FFAB0029",
                "#FF563029",
                "#FF563029",
            ];
            ctx.save();
            // Draw horizontal dotted lines
            ctx.setLineDash([3, 4]);
            ctx.lineWidth = 1;
            yScale.ticks.forEach((tick, index) => {
                const y = yScale.getPixelForValue(tick.value);
                ctx.strokeStyle = borderColors[index % borderColors.length];
                ctx.beginPath();
                ctx.moveTo(xScale.left, y);
                ctx.lineTo(xScale.right, y);
                ctx.stroke();
            });
            // Draw colored Y-axis labels
            ctx.setLineDash([]);
            ctx.textAlign = "right";
            ctx.textBaseline = "middle";
            ctx.font = `normal ${isMobile ? 13 : 14}px Arial`;
            yScale.ticks.forEach((tick, index) => {
                const y = yScale.getPixelForValue(tick.value);
                const color = colors[index % colors.length];
                ctx.fillStyle = color;
                ctx.fillText(tick.value.toString(), xScale.left - 8, y);
            });
            ctx.restore();
        },
    };
    // Prepare data for Chart.js using time as labels
    const chartData = {
        labels: data.map((item) => item.time),
        datasets: [
            {
                label: "Temperature",
                data: data.map((item) => item.avgTemperature),
                borderColor: "#3B3561",
                borderWidth: 3,
                pointRadius: 4,
                pointBackgroundColor: "#3B3561",
                pointBorderColor: "#FFFFFF",
                pointBorderWidth: 2,
                pointHoverRadius: 8,
                pointHoverBackgroundColor: "#3B3561",
                pointHoverBorderColor: "#FFFFFF",
                pointHoverBorderWidth: 3,
                tension: 0.4,
                fill: false,
            },
            // Alarm indicators dataset
            {
                label: "Alarms",
                data: data.map((item) => item.hasAlarms ? item.avgTemperature : null),
                borderColor: "transparent",
                backgroundColor: "#FF5630",
                pointRadius: data.map((item) => item.hasAlarms ? 6 : 0),
                pointHoverRadius: data.map((item) => item.hasAlarms ? 8 : 0),
                pointBorderColor: "#FFFFFF",
                pointBorderWidth: 2,
                showLine: false,
            },
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
                backgroundColor: "rgba(59, 53, 97, 0.95)",
                titleColor: "#FFFFFF",
                titleFont: {
                    size: 14,
                    weight: 'bold',
                },
                bodyColor: "#FFFFFF",
                bodyFont: {
                    size: 13,
                },
                borderColor: "#3B3561",
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                displayColors: false,
                callbacks: {
                    title: function (context) {
                        var _a;
                        return ((_a = context[0]) === null || _a === void 0 ? void 0 : _a.label) || '';
                    },
                    label: function (context) {
                        var _a, _b;
                        // Skip the Alarms dataset label
                        if (context.datasetIndex === 1)
                            return;
                        const index = context.dataIndex;
                        const dataPoint = data[index];
                        if (!dataPoint)
                            return;
                        const lines = [];
                        lines.push(`Avg Temperature: ${(_b = (_a = dataPoint.avgTemperature) === null || _a === void 0 ? void 0 : _a.toFixed(1)) !== null && _b !== void 0 ? _b : 'N/A'}°C`);
                        if (dataPoint.minTemperature != null) {
                            lines.push(`Min Temperature: ${dataPoint.minTemperature.toFixed(1)}°C`);
                        }
                        if (dataPoint.maxTemperature != null) {
                            lines.push(`Max Temperature: ${dataPoint.maxTemperature.toFixed(1)}°C`);
                        }
                        lines.push(`Total Readings: ${dataPoint.count.toLocaleString()}`);
                        if (dataPoint.hasAlarms) {
                            lines.push(`⚠️ Alarms Generated`);
                        }
                        return lines;
                    },
                },
            },
            dottedGrid: {},
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: theme.palette.text.secondary,
                    font: {
                        size: isMobile ? 11 : 12,
                    },
                    maxRotation: 0,
                    padding: isMobile ? 5 : 10,
                },
            },
            y: {
                display: true,
                min: 0,
                max: 100,
                grid: {
                    display: false,
                    drawBorder: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    display: false,
                    stepSize: 25,
                },
            },
        },
        layout: {
            padding: {
                left: isMobile ? 30 : 35,
                right: isMobile ? 5 : 10,
                top: isMobile ? 5 : 10,
                bottom: isMobile ? 5 : 10,
            },
        },
    };
    return (React.createElement(FCard, null,
        React.createElement(Box, { py: 1.2, px: 1 },
            React.createElement(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: { xs: 2, sm: 3 },
                } },
                React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                    React.createElement(TemperatureIcon, { size: isMobile ? 20 : 28 }),
                    React.createElement(Typography, { variant: isMobile ? "subtitle1" : "h6", color: "text.primary" }, title)),
                React.createElement(SIconButton, { colorVariant: "primary", size: "small", title: "Temperature Information", showBackground: false },
                    React.createElement(InfoCircle, { size: isMobile ? 18 : 20 }))),
            React.createElement(Box, { sx: {
                    width: "100%",
                    height: { xs: 250, sm: 300 },
                    maxHeight: 253,
                } },
                React.createElement(Line, { data: chartData, options: options, plugins: [dottedGridPlugin] })),
            React.createElement(Box, { sx: Object.assign(Object.assign({}, centered), { gap: 1 }) },
                React.createElement(Box, { sx: {
                        width: isMobile ? 6 : 8,
                        height: isMobile ? 6 : 8,
                        borderRadius: "50%",
                        backgroundColor: "#FF5630",
                    } }),
                React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "Alarms Generated")))));
};
